import { ref, onMounted, defineComponent, PropType } from '@vue/composition-api'
import AddCompanySubscription from '@/components/admin-add-company-subscription/admin-add-company-subscription.vue';
import {
  CompanySubscription,
  SubscriptionTemplate,
  SubscriptionPeriod,
  SubscriptionCapability,
  InvoiceCreationPeriod,
  getCompanySubscriptions,
  getSubscriptionTemplates,
  expireCompanySubscription
} from '@/functions/subscription-functions';
import { allPromotions } from '@/states/promotion-state';
import moment from 'moment';
import { Company } from '@/views/admin-companies/admin-companies-types';
import { getPromotionTitle } from '@/utilities/promotion-utilities';
import { i18n } from '@/utilities/vue-i18n';

export default defineComponent({
  components: { AddCompanySubscription },

  props: {
    companyId: {
      type: Number,
      required: true
    },
    company: {
      type: Object as PropType<Company>,
      required: false
    }
  },

  setup(props: { companyId: number; company?: Company }, context) {
    const subscriptions = ref<CompanySubscription[]>([])
    const templates = ref<SubscriptionTemplate[]>([])
    const showAddModal = ref(false)
    const loading = ref(false)
    const showDetailsModal = ref(false)
    const selectedSubscription = ref<CompanySubscription | null>(null)
    const selectedCapability = ref<SubscriptionCapability | null>(null)

    const fetchSubscriptions = async () => {
      try {
        loading.value = true
        subscriptions.value = await getCompanySubscriptions(props.companyId)
      } catch (error) {
        console.error('Failed to fetch subscriptions:', error)
      } finally {
        loading.value = false
      }
    }

    const fetchTemplates = async () => {
      try {
        templates.value = await getSubscriptionTemplates()
      } catch (error) {
        console.error('Failed to fetch templates:', error)
      }
    }

    const expireSubscription = async (subscriptionId: string) => {
      try {
        await expireCompanySubscription(subscriptionId)
        await fetchSubscriptions()
      } catch (error) {
        console.error('Failed to expire subscription:', error)
      }
    }

    const formatPrice = (price: number) => {
      return new Intl.NumberFormat('sv-SE', {
        style: 'currency',
        currency: 'SEK'
      }).format(price)
    }

    const formatPeriod = (period: SubscriptionPeriod) => {
      if (period.type === 'TimeLimitedSubscriptionPeriod') {
        return `${moment(period.startDate).format('YYYY-MM')} - ${moment(period.endDate).format('YYYY-MM')}`
      }
      return `${moment(period.startDate).format('YYYY-MM')} - --`
    }

    const statusClass = (subscription: CompanySubscription) => ({
      'text-success': !subscription.isExpired,
      'text-danger': subscription.isExpired
    })

    const handleSubscriptionAdded = () => {
      fetchSubscriptions()
      showAddModal.value = false
    }

    const openCapabilityDetails = (subscription: CompanySubscription, capability: SubscriptionCapability) => {
      selectedSubscription.value = subscription
      selectedCapability.value = capability
      showDetailsModal.value = true
    }

    const closeDetailsModal = () => {
      showDetailsModal.value = false
      selectedSubscription.value = null
      selectedCapability.value = null
    }

    const formatInvoiceCreationPeriod = (invoiceCreationPeriod?: InvoiceCreationPeriod) => {
      switch (invoiceCreationPeriod) {
        case InvoiceCreationPeriod.weekly:
          return i18n.t('pages.subscription.invoice-creation-period-weekly') as string
        case InvoiceCreationPeriod.semiMonthly:
          return i18n.t('pages.subscription.invoice-creation-period-semi-monthly') as string
        case InvoiceCreationPeriod.monthly:
          return i18n.t('pages.subscription.invoice-creation-period-monthly') as string
        case InvoiceCreationPeriod.quarterly:
          return i18n.t('pages.subscription.invoice-creation-period-quarterly') as string
        case InvoiceCreationPeriod.none:
        default:
          return i18n.t('pages.subscription.invoice-creation-period-none') as string
      }
    }

    onMounted(async () => {
      await Promise.all([fetchSubscriptions(), fetchTemplates()])
    })

    return {
      subscriptions,
      templates,
      showAddModal,
      loading,
      expireSubscription,
      formatPrice,
      formatPeriod,
      statusClass,
      handleSubscriptionAdded,
      showDetailsModal,
      selectedSubscription,
      selectedCapability,
      openCapabilityDetails,
      closeDetailsModal,
      formatInvoiceCreationPeriod,
      allPromotions,
      // For creating new subscriptions - uses company prop
      getPromotionTitle: (promotionType: string) => {
        return getPromotionTitle(promotionType, i18n.t.bind(i18n), props.company?.volumePackageIsLight);
      },
      // For displaying existing subscription details - uses subscription's companyVolumePackageIsLight
      getPromotionTitleForSubscription: (promotionType: string) => {
        return getPromotionTitle(promotionType, i18n.t.bind(i18n), selectedSubscription.value?.companyVolumePackageIsLight);
      }
    }
  }
});